import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  type OnDestroy,
  type OnInit,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { NAME_PATTERN } from '@fcom/core/utils';
import { AnalyticsFormStatus, FormInputType, FormStatus } from '@fcom/service-forms';
import { ButtonMode, ButtonTheme, ButtonType, NotificationLayout, NotificationTheme } from '@fcom/ui-components';

import { RetrieveFields, type CaseGetMessage, type RetrieveCase } from '../../../interfaces';
import { ClaimOrFeedbackService } from '../../../services/claim-or-feedback.service';
import { CASE_NUMBER_PATTERN } from '../../../utils';

@Component({
  selector: 'fin-retrieve-page-content',
  templateUrl: './retrieve-page-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetrievePageContentComponent implements OnInit, OnDestroy {
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly FormInputType = FormInputType;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly RetrieveFields = RetrieveFields;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  form: UntypedFormGroup = new UntypedFormGroup({});
  group: UntypedFormGroup;
  formStatus: FormStatus = FormStatus.INITIAL;
  responseContent: RetrieveCase;

  @Input()
  additionalInfoTitle: string;

  @Input()
  additionalInfoText: string;

  constructor(
    private fb: UntypedFormBuilder,
    private claimOrFeedbackService: ClaimOrFeedbackService,
    private location: Location,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private gtmService: GtmService
  ) {}

  private subscriptions: Subscription = new Subscription();

  backToForm(): void {
    this.formStatus = FormStatus.INITIAL;
  }

  backToPreviousPage(): void {
    this.location.back();
  }

  ngOnInit(): void {
    const caseNumber = this.activatedRoute.snapshot.queryParams?.[RetrieveFields.CASE_NUMBER];

    const caseFieldValue = this.form?.get(RetrieveFields.CASE_NUMBER)?.value;

    this.group = this.fb.group({
      [RetrieveFields.FAMILY_NAME]: [
        this.form?.get(RetrieveFields.FAMILY_NAME)?.value || '',
        [Validators.required, Validators.pattern(NAME_PATTERN)],
      ],
      [RetrieveFields.CASE_NUMBER]: [
        caseNumber ?? caseFieldValue ?? '',
        [Validators.pattern(CASE_NUMBER_PATTERN), Validators.required],
      ],
    });

    this.form.setControl(RetrieveFields.RETRIEVE_FORM, this.group);

    this.router.navigate([], { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  retrieveCase(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;

    this.subscriptions.add(
      this.claimOrFeedbackService
        .fetchCase(this.form.getRawValue())
        .pipe(take(1))
        .subscribe({
          error: (error: HttpErrorResponse): void => {
            const errorMessage = error.error?.message || error.error?.error || error.error || error.message;
            this.formStatus =
              error?.status === 400 && errorMessage?.toLowerCase()?.includes('refund case is not supported')
                ? FormStatus.REFUND_CASE_NOT_SUPPORTED
                : FormStatus.ERROR;
            this.trackElement(AnalyticsFormStatus.ERROR);
            this.cdr.markForCheck();
          },
          next: (value: RetrieveCase): void => {
            this.responseContent = {
              ...value,
              messages: value?.messages ? [...value.messages].sort(this.sortByCreationDate) : [],
            };
            this.trackElement(AnalyticsFormStatus.SUCCESS);
            this.formStatus = FormStatus.COMPLETE;
            this.cdr.markForCheck();
          },
        })
    );
  }

  private sortByCreationDate(a: CaseGetMessage, b: CaseGetMessage): number {
    return a?.creationDate > b?.creationDate ? 1 : -1;
  }

  private trackElement(status: AnalyticsFormStatus): void {
    this.gtmService.trackElement(
      `retrieve-${status}`,
      GaContext.RETRIEVE_CASE_FORM,
      ElementTypes.FORM,
      undefined,
      ElementActions.SUBMIT
    );
  }
}
